import React, { useEffect, useMemo, useRef, useState } from "react";
import makeStyles from "@mui/styles/makeStyles";
//import useImageUpload from "../../../hooks/imageupload";
import { Trans, useTranslation } from "react-i18next";
import useTemplateInstance from "../../../hooks/useTemplateInstance";
import { getUserImageResources } from "@/react/Providers/TemplateInstanceProvider";
import EpmsImage from "../../../../../components/Common/EpmsImage";
//import UploadProgressDialog from "../../UploadProgressDialog";
import Icon from "@mui/material/Icon";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import DialogContentText from "@mui/material/DialogContentText";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { usePictureSets } from "../../../../hooks/usePictureSets";
import PictureSetsDialog from "./PictureSetsDialog";
import { useRecentImages } from "../../../Providers/RecentImagesProvider";
import useImageUpload from "../../../../../hooks/useImageUpload";
import UploadProgressDialog from "./UploadProgressDialog";
import { usePortal } from "@/react/Providers/PortalProvider";
import MyPictureSetsIcon from "../../../../../components/Common/MyPictureSetsIcon";
import { alpha } from "@mui/material/styles";
import Popup, {
  PopupActions,
  PopupButton,
  PopupContent,
  PopupHeading,
  PopupTextContent,
} from "../../../../../components/Common/Popup";
import useCopyrightDialog from "@/react/Webshop/hooks/useCopyrightDialog";
import useDialogBackButton from "@/react/hooks/useDialogBackButton";

import ImageAllowedValidator from "../../../../../components/Template/ImageAllowedValidator";
import ImageNotAllowedException from "../../../../../components/Template/ImageNotAllowedException";
import { isGroupPicture, isMiniGroupPicture } from "@/react/lib/imageutils";
import RetouchedIcon from "@mui/icons-material/Brush";
import { sortSubjectPictures } from "@/react/utils";
import { useGreenScreenBackgrounds } from "@/react/Webshop/Mobile/Providers/GreenScreenBackgroundsProvider";

const useStyles = makeStyles((theme) => ({
  toolbarItems: {
    listStyle: "none",
    margin: 0,
    padding: 0,
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    maxWidth: "100%",
    overflowX: "auto",
    display: "flex",
    flexWrap: "nowrap",
    "& > li.image-container-disabled": {
      filter: "grayscale(1)",
      opacity: 0.3,
    },
    "& > li.image-container > button": {
      height: "104px",
      display: "block",
      marginLeft: theme.spacing(1),
      backgroundColor: "transparent",
      backgroundSize: "cover",
      backgroundPosition: "center center",
      border: "1px solid " + alpha(theme.palette.primary.dark, 0.5),
      boxSizing: "border-box",
      padding: 0,
      borderRadius: "0",
      overflow: "hidden",
      position: "relative",
      "& > img, & > canvas": {
        height: "104px",
      },
    },
  },
  inboxContainer: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(0),
  },
  selectedImage: {
    position: "absolute",
    left: 0,
    top: 0,
    width: "100%",
    height: "100%",
    backgroundColor: theme.palette.primary.main + "aa",
  },
  noImagesContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    "& > div": {
      // Two 80px buttons precede this cell when own pictures are the source,
      // leaving about 100px of text width on a 320px screen. The narrower
      // gutter and the 14px size (same as the "Picture id" hint above the
      // inbox) keep translations of up to about 50 characters within the
      // buttons' 104px height.
      paddingRight: theme.spacing(2),
      paddingLeft: theme.spacing(2),
      boxSizing: "border-box",
      textAlign: "center",
      fontSize: "14px",
    },
  },
  addImagesButton: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    width: "80px",
    height: "104px",
    borderRadius: "0px",
    border: "none",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    textAlign: "center",
    "& > svg, & > span": {
      width: "30px",
      marginBottom: theme.spacing(1),
    },
    "& > div": {
      fontSize: "12px",
    },
    "&.upload-button": {
      marginLeft: theme.spacing(1),
    },
  },
  imageIsUsed: {
    position: "absolute",
    right: "2px",
    bottom: "2px",
    color: "rgba(255, 255, 255, 0.8)",
    "& span": {
      width: "1em",
      height: "1em",
      fontSize: "1rem",
    },
  },
  imageIsRetouched: {
    position: "absolute",
    left: "2px",
    top: "2px",
    color: theme.palette.primary.main,
    "& svg": {
      width: "1em",
      height: "1em",
      fontSize: "1rem",
      filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.4))",
    },
  },
}));

const ownPictureIsDefault = (portal, template) => {
  if (!portal.options.allowOwnPhotos) {
    return false;
  }

  const ownPhotosCategories =
    portal.webshop.template.mobileInboxOwnPhotosAsDefaultCategories;

  for (const category of ownPhotosCategories) {
    if (template.categories.indexOf(category) >= 0) {
      return true;
    }
  }
  return false;
};

const ImageInbox = ({
  handleSelectImage,
  resource,
  allowSubjectChange,
  template,
}) => {
  const classes = useStyles();
  const [state] = useTemplateInstance();
  const { image } = resource || { image: {} };
  const [notAllUploadedDialog, setNotAllUploadedDialog] = useState(false);
  const { currentPictureSet } = usePictureSets();
  const [chooseImageSource, setChooseImageSource] = useState(false);
  const portal = usePortal();
  const [sourceType, setSourceType] = useState(
    ownPictureIsDefault(portal, template) ? "recent-images" : "picture-set",
  );
  const { recentImages: ri, addImage } = useRecentImages();
  const fileRef = useRef();
  const [imageNotSupportedDialogOpen, setImageNotSupportedDialogOpen] =
    useState(null);
  const { getCopyrightTriggerProps, copyrightDialog } = useCopyrightDialog();

  const recentImages = useMemo(() => {
    let pictures = [];
    if (sourceType === "picture-set") {
      if (!currentPictureSet || !resource) {
        return {
          allowed: [],
          notAllowed: [],
        };
      }
      pictures = currentPictureSet.pictures;
    } else if (resource) {
      pictures = ri;
    } else {
      // We sometimes end up here?
      console.log("Does this happened?");
      return {
        allowed: [],
        notAllowed: [],
      };
    }

    const imageAllowedValidator = new ImageAllowedValidator(
      template,
      portal.webshop.template,
    );

    return pictures.reduce(
      (pictures, image) => {
        console.log("The resource is", resource);

        try {
          imageAllowedValidator.imageAllowedInResource(image, resource);
          pictures.allowed.push(image);
        } catch (err) {
          if (err instanceof ImageNotAllowedException) {
            console.log("Image is not allowed", err);
            pictures.notAllowed.push(image);
          }
        }
        return pictures;
      },
      {
        allowed: [],
        notAllowed: [],
      },
    );
  }, [currentPictureSet, sourceType, resource, ri]);

  useEffect(() => {
    console.log("File ref: Source type " + sourceType);
    console.log("File ref", fileRef);

    // If we change source type to recent imgages, fire ref click
    if (sourceType === "recent-images") {
      if (recentImages.length == 0) {
        fileRef.current.click();
      }
    }
  }, [sourceType]);

  console.log("Recent images", recentImages);

  const onUploadComplete = (uploadedImages, allUploaded) => {
    if (!allUploaded) {
      setNotAllUploadedDialog(true);
    }

    const images = uploadedImages.map((image) => {
      return {
        uuid: image.uuid,
        size: image.dimensions,
        keywords: ["user_uploaded"],
      };
    });

    for (const image of images) {
      console.log("Uploaded image", image);
      addImage(image);
    }

    if (images.length == 1) {
      const image = images[0];

      console.log("UPLOAD Trying to set image", image);
      const imageAllowedValidator = new ImageAllowedValidator(
        template,
        portal.webshop.template,
      );

      try {
        imageAllowedValidator.imageAllowedInResource(image, resource);
        changeImage(image);
        console.log("UPLOAD Successfully changed image");
      } catch (err) {
        console.log("UPLOAD Image error", image, err);
        setImageNotSupportedDialogOpen(true);
      }
    }
  };

  const { totalProgress, isUploading, retrying, bind } = useImageUpload(
    null,
    onUploadComplete,
  );

  const usedImageUuids = useMemo(() => {
    if (!resource) {
      return [];
    }
    const resources = getUserImageResources(state);
    const uuids = resources
      .filter((r) => {
        // Don't add the current resource as we work on a local version:
        return r?.image?.uuid && resource.fullId != r.fullId;
      })
      .map((resource) => {
        return resource.image.uuid;
      });
    if (image?.uuid) {
      uuids.push(image.uuid);
    }
    return uuids;
  }, [resource]);

  const handleAddButtonClick = () => {
    setChooseImageSource(true);
  };

  const handleUploadButtonClick = () => {
    fileRef.current.click();
  };

  const changeImage = (image) => {
    console.log("Changing image to ", image);
    handleSelectImage(image);
  };

  const imageNotSupported = (image) => {
    setImageNotSupportedDialogOpen(image);
  };

  return (
    <div className={classes.inboxContainer}>
      <input
        type="file"
        ref={fileRef}
        {...bind}
        multiple={true}
        style={{ position: "absolute", left: "-9000" }}
      />
      <ul className={classes.toolbarItems}>
        <li>
          <button
            className={classes.addImagesButton}
            onClick={handleAddButtonClick}
          >
            <MyPictureSetsIcon color="white" />
            <div>
              <Trans>Change picture set</Trans>
            </div>
          </button>
        </li>
        {sourceType == "recent-images" && (
          <li>
            <button
              className={`${classes.addImagesButton} upload-button`}
              onClick={handleUploadButtonClick}
            >
              <Icon>file_upload</Icon>
              <div>
                <Trans>Upload</Trans>
              </div>
            </button>
          </li>
        )}
        <ImageList
          recentImages={recentImages}
          pictureSet={sourceType == "recent-images" ? null : currentPictureSet}
          usedImageUuids={usedImageUuids}
          currentImage={image}
          changeImage={changeImage}
          imageNotSupported={imageNotSupported}
          onImageContextMenu={getCopyrightTriggerProps().onContextMenu}
        />

        {recentImages.allowed.length + recentImages.notAllowed.length == 0 && (
          <li className={classes.noImagesContainer}>
            <div>
              <div>
                {/* With own pictures as the source the Upload button sits next
                    to "Change picture set", so point at both instead of just
                    stating that the inbox is empty (EPMS-2488). */}
                {sourceType == "recent-images" ? (
                  <Trans>Select a picture set or upload a picture</Trans>
                ) : (
                  <Trans>The image inbox is empty</Trans>
                )}
              </div>
            </div>
          </li>
        )}
      </ul>

      {copyrightDialog}

      {isUploading && (
        <UploadProgressDialog
          retrying={retrying}
          totalProgress={totalProgress}
        />
      )}
      {notAllUploadedDialog && (
        <NotAllImagesUploadedDialog
          onClose={() => setNotAllUploadedDialog(false)}
        />
      )}
      {chooseImageSource && (
        <PictureSetsDialog
          onClose={() => setChooseImageSource(false)}
          sourceType={sourceType}
          allowSubjectChange={allowSubjectChange}
          onSetSourceType={(type) => setSourceType(type)}
        />
      )}
      {imageNotSupportedDialogOpen && (
        <PictureNotAllowedDialog
          image={imageNotSupportedDialogOpen}
          onClose={() => setImageNotSupportedDialogOpen(null)}
        />
      )}
    </div>
  );
};

const NotAllImagesUploadedDialog = ({ onClose }) => {
  // Hardware/browser back closes the dialog instead of navigating away.
  useDialogBackButton(true, onClose);

  return (
    <Dialog open={true} onClose={onClose}>
      <DialogTitle>
        <Trans>Upload failed</Trans>
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          <Trans>
            At least one of the files couldn't be uploaded due to an issue with
            your Internet connection.
          </Trans>
        </DialogContentText>
        <DialogContentText>
          <Trans>Please check your connection and try again.</Trans>
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={onClose}>
          <Trans>Ok</Trans>
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ImageContainer = ({
  onClick,
  userImage,
  isSelected,
  usedImageUuids,
  label,
  disabled,
  backgroundUrl,
  backgroundColor,
  onImageContextMenu,
}) => {
  const isUsed = useMemo(() => {
    return usedImageUuids.indexOf(userImage.uuid) >= 0;
  }, [usedImageUuids, userImage]);

  const isRetouched = useMemo(() => {
    return userImage.keywords.indexOf("retouched") >= 0;
  }, [userImage]);

  console.log("Image container", userImage);

  const classes = useStyles();

  // NOTE: Setting alt to '' should make screen readers ignore it, as
  // the button have aria-label set
  return (
    <button
      onClick={() => onClick(userImage)}
      aria-label={label}
      aria-current={isSelected ? "true" : "false"}
      aria-disabled={disabled ? "true" : undefined}
      onContextMenu={onImageContextMenu}
    >
      <EpmsImage
        uuid={userImage.uuid}
        size={320}
        alt=""
        style={{
          backgroundImage: backgroundUrl ? `url(${backgroundUrl})` : undefined,
          backgroundColor,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      {isSelected && <div className={classes.selectedImage} />}
      {isUsed && (
        <div className={classes.imageIsUsed}>
          <Icon>check_circle</Icon>
        </div>
      )}
      {isRetouched && (
        <div className={classes.imageIsRetouched}>
          <RetouchedIcon />
        </div>
      )}
    </button>
  );
};

const PictureNotAllowedDialog = ({ onClose, image }) => {
  console.log("Not allowed", image);
  return (
    <Popup open={true} onClose={onClose}>
      <PopupHeading>
        <Trans>Picture not allowed</Trans>
      </PopupHeading>

      <PopupContent>
        <PopupTextContent>
          <Trans>You can't use this image in this image field</Trans>
          {image.webshopId && (
            <Box mb={2} mt={2} style={{ color: "#7d7d7d" }}>
              <Trans>Picture id:</Trans> {image.webshopId}
            </Box>
          )}
        </PopupTextContent>
      </PopupContent>
      <PopupActions>
        <PopupButton onClick={onClose}>
          <Trans>Ok</Trans>
        </PopupButton>
      </PopupActions>
    </Popup>
  );
};

const incrementPictureTypeCount = (numPicturesByType, type) => {
  numPicturesByType[type] = (numPicturesByType[type] ?? 0) + 1;
  return numPicturesByType[type];
};

const generateLabel = (userImage, pictureSet, numPicturesByType, t) => {
  let label = pictureSet ? `${pictureSet.subject.name} - ` : "";
  const pictureType = isGroupPicture(userImage)
    ? "groupPictures"
    : isMiniGroupPicture(userImage)
      ? "miniGroupPictures"
      : "individualPictures";

  const count = incrementPictureTypeCount(numPicturesByType, pictureType);
  const translationKey =
    pictureType === "individualPictures"
      ? "Portrait picture %d"
      : pictureType === "miniGroupPictures"
        ? "Mini picture %d"
        : "Group picture %d";

  return (label += t(translationKey, {
    postProcess: "sprintf",
    sprintf: [count],
  }));
};

const ImageList = ({
  recentImages,
  currentImage,
  usedImageUuids,
  changeImage,
  imageNotSupported,
  pictureSet,
  onImageContextMenu,
}) => {
  const [t] = useTranslation();
  const portal = usePortal();
  const inboxSettings = portal.webshop.inboxSettings;
  const [backgrounds, loadingBackgrounds] = useGreenScreenBackgrounds();
  let defaultBackground = null;
  const backgroundColor = inboxSettings.pictureBackgroundColor;
  if (inboxSettings.pictureUseDefaultBackground && backgrounds) {
    console.log("Green screen backgrounds: ", backgrounds);
    defaultBackground = backgrounds.find((gsb) => gsb.isDefault);
  }

  let numPicturesByType = {};
  const listItems = [];

  const allImages = useMemo(() => {
    const sortedAllowedPictures = sortSubjectPictures(recentImages.allowed);
    const sortedNotAllowedPictures = sortSubjectPictures(
      recentImages.notAllowed,
    );
    return [...sortedAllowedPictures, ...sortedNotAllowedPictures];
  }, [recentImages]);

  allImages.forEach((userImage) => {
    const isNotAllowed = recentImages.notAllowed.includes(userImage);
    const label = isNotAllowed
      ? `${t("Not allowed picture")} - ${generateLabel(userImage, pictureSet, numPicturesByType, t)}`
      : generateLabel(userImage, pictureSet, numPicturesByType, t);
    let backgroundUrl = null;

    if (defaultBackground) {
      backgroundUrl = `/system/gsbackground.php?uuid=${defaultBackground.uuid}&rotation=0&effect=original`;
      if (userImage.size.width > userImage.size.height) {
        backgroundUrl += "&orientation=landscape";
      }
    }

    console.log("Is selected", currentImage?.uuid, userImage.uuid);
    listItems.push(
      <li
        key={userImage.uuid}
        className={`image-container ${isNotAllowed ? "image-container-disabled" : ""}`}
      >
        <ImageContainer
          label={label}
          userImage={userImage}
          onClick={isNotAllowed ? imageNotSupported : changeImage}
          disabled={isNotAllowed}
          isSelected={currentImage && currentImage.uuid === userImage.uuid}
          usedImageUuids={usedImageUuids}
          backgroundUrl={backgroundUrl}
          backgroundColor={backgroundColor}
          onImageContextMenu={onImageContextMenu}
        />
      </li>,
    );
  });

  return <>{listItems}</>;
};

export default ImageInbox;
